import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { CreditCard, XCircle, CheckCircle, Home, Loader2 } from 'lucide-react';
import { useGame } from '../context/GameContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionManagement = () => {
  const navigate = useNavigate();
  const { playerName } = useGame();
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!playerName) {
      navigate('/');
      return;
    }
    fetchSubscription();
  }, [playerName, navigate]);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/player/${playerName}/subscription`);
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setError('Failed to load subscription details');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access to all games.')) {
      return;
    }

    try {
      setCanceling(true);
      setError('');
      const response = await axios.post(`${API}/player/${playerName}/cancel-subscription`);
      setSuccess('Subscription cancelled successfully');
      setSubscription(response.data);
      
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error) {
      console.error('Error canceling subscription:', error);
      setError(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCanceling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{
        background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)'
      }}>
        <Card className="p-12 bg-white/95 backdrop-blur-sm shadow-2xl text-center" style={{ borderRadius: '32px' }}>
          <Loader2 className="w-16 h-16 mx-auto mb-4 text-red-500 animate-spin" />
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Loading...</h2>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8" style={{
      background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)'
    }}>
      <div className="max-w-2xl mx-auto">
        <Button
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-6 text-lg font-semibold text-white hover:scale-110 transition-transform"
        >
          <Home className="w-5 h-5 mr-2" />
          Home
        </Button>

        <Card className="p-8 md:p-12 bg-white/95 backdrop-blur-sm shadow-2xl" style={{ borderRadius: '32px' }}>
          <div className="text-center mb-8">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-red-400 to-orange-400 rounded-full flex items-center justify-center">
              <CreditCard className="w-14 h-14 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Subscription Management</h1>
            <p className="text-xl text-gray-600">Manage your Little Genius Hub subscription</p>
          </div>

          {error && (
            <Card className="p-4 mb-6 bg-red-50 border-2 border-red-300" style={{ borderRadius: '16px' }}>
              <p className="text-red-700 text-center">{error}</p>
            </Card>
          )}

          {success && (
            <Card className="p-4 mb-6 bg-green-50 border-2 border-green-300" style={{ borderRadius: '16px' }}>
              <div className="flex items-center justify-center gap-2 text-green-700">
                <CheckCircle className="w-5 h-5" />
                <p>{success}</p>
              </div>
            </Card>
          )}

          <Card className="p-8 bg-gradient-to-br from-red-50 to-orange-50 border-4 border-red-200 mb-8" style={{ borderRadius: '24px' }}>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-700">Player Name:</span>
                <span className="text-xl font-bold text-gray-800">{playerName}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-700">Plan:</span>
                <span className="text-xl font-bold text-gray-800">Monthly Subscription</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-700">Price:</span>
                <span className="text-xl font-bold text-red-600">$9.99/month</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-700">Status:</span>
                {subscription?.subscription_status === 'active' ? (
                  <span className="flex items-center gap-2 text-green-600 font-bold">
                    <CheckCircle className="w-5 h-5" />
                    Active
                  </span>
                ) : (
                  <span className="flex items-center gap-2 text-red-600 font-bold">
                    <XCircle className="w-5 h-5" />
                    Inactive
                  </span>
                )}
              </div>
            </div>
          </Card>

          {subscription?.subscription_status === 'active' ? (
            <div className="space-y-4">
              <Card className="p-6 bg-yellow-50 border-2 border-yellow-300" style={{ borderRadius: '16px' }}>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Cancel Subscription</h3>
                <p className="text-gray-700 mb-4">
                  If you cancel your subscription, you will immediately lose access to all games and learning content.
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 mb-4">
                  <li>No refunds for unused time</li>
                  <li>Progress and achievements will be saved</li>
                  <li>You can re-subscribe anytime</li>
                </ul>
              </Card>

              <Button
                onClick={handleCancel}
                disabled={canceling}
                className="w-full py-6 text-xl font-bold transition-all duration-300 hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                  borderRadius: '16px'
                }}
              >
                {canceling ? (
                  <>
                    <Loader2 className="w-6 h-6 mr-2 animate-spin" />
                    Canceling...
                  </>
                ) : (
                  <>
                    <XCircle className="w-6 h-6 mr-2" />
                    Cancel Subscription
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-gray-700 mb-6 text-lg">You don't have an active subscription.</p>
              <Button
                onClick={() => navigate('/payment')}
                className="px-12 py-6 text-xl font-bold transition-all duration-300 hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                  borderRadius: '16px'
                }}
              >
                Subscribe Now
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default SubscriptionManagement;
